import { http, HttpResponse } from 'msw';
import {
  mockEnterprises,
  mockProducts,
  mockProcessingBatches,
  mockQualityRecords,
  mockOrders,
  mockMesProductionTasks,
  mockMesProcessingTasks,
} from '../fixtures/processing';
import type { Enterprise, ProductItem, ProcessingBatch, QualityRecord, TraceOrder, MesProductionTask, MesProcessingTask } from '@trace/shared/types';
import { getOrdersStore, setOrdersStore } from './_sharedStores';

let enterprisesStore = [...mockEnterprises];
let productsStore = [...mockProducts];
let batchesStore = [...mockProcessingBatches];
// ordersStore 使用共享模块以保持跨模块数据一致性
let productionTasksStore: any[] = [...mockMesProductionTasks];
let processingTasksStore: any[] = [...mockMesProcessingTasks];
const qualityStore: Record<string, QualityRecord> = { ...mockQualityRecords };

export const processingHandlers = [
  // ================= Enterprises API =================
  http.get('/api/enterprises', ({ request }) => {
    const url = new URL(request.url);
    const keyword = url.searchParams.get('keyword')?.toLowerCase();
    const status = url.searchParams.get('status');

    let list = [...enterprisesStore];
    if (status && status !== 'all') {
      list = list.filter((e) => e.status === status);
    }
    if (keyword) {
      list = list.filter(
        (e) =>
          e.name.toLowerCase().includes(keyword) ||
          (e.brandName && e.brandName.toLowerCase().includes(keyword)) ||
          e.enterpriseCode.toLowerCase().includes(keyword) ||
          e.contactPerson.toLowerCase().includes(keyword) ||
          (e.creditCode && e.creditCode.toLowerCase().includes(keyword)),
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.post('/api/enterprises', async ({ request }) => {
    const body = (await request.json()) as Partial<Enterprise>;
    const newEnt: Enterprise = {
      id: `ent_${Date.now()}`,
      name: body.name || '新生产加工企业',
      brandName: body.brandName || '自有品牌',
      code: body.code || `ENT-${Date.now().toString().slice(-4)}`,
      logo: body.logo || 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=120&auto=format&fit=crop&q=80',
      contactPerson: body.contactPerson || '负责人',
      contactPhone: body.contactPhone || '13800000000',
      status: body.status || 'active',
      enterpriseCode: body.enterpriseCode || `MA.156.A1.00${enterprisesStore.length + 60}`,
      allocatedQuota: body.allocatedQuota || 100000,
      usedQuota: 0,
      licenseNumber: body.licenseNumber || 'SC10123018400000',
      creditCode: body.creditCode || '91230184MA00000000',
      address: body.address || '黑龙江省五常市',
      remarks: body.remarks || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    enterprisesStore = [newEnt, ...enterprisesStore];

    return HttpResponse.json({
      code: 200,
      message: '企业档案已创建并成功分配 MA 专属溯源码',
      data: newEnt,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.put('/api/enterprises/:id', async ({ params, request }) => {
    const body = (await request.json()) as Partial<Enterprise>;
    const idx = enterprisesStore.findIndex((e) => e.id === params.id);
    if (idx !== -1) {
      enterprisesStore[idx] = {
        ...enterprisesStore[idx]!,
        ...body,
        updatedAt: new Date().toISOString(),
      };
      return HttpResponse.json({
        code: 200,
        message: '企业档案更新成功',
        data: enterprisesStore[idx],
        success: true,
        timestamp: new Date().toISOString(),
      });
    }
    return HttpResponse.json({ code: 404, message: 'Enterprise not found', success: false }, { status: 404 });
  }),

  http.delete('/api/enterprises/:id', ({ params }) => {
    enterprisesStore = enterprisesStore.filter((e) => e.id !== params.id);
    return HttpResponse.json({
      code: 200,
      message: '企业档案已删除',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // ================= Products API =================
  http.get('/api/products', ({ request }) => {
    const url = new URL(request.url);
    const enterpriseId = url.searchParams.get('enterpriseId');
    const status = url.searchParams.get('status');
    const keyword = url.searchParams.get('keyword')?.toLowerCase();

    let list = [...productsStore];
    if (enterpriseId && enterpriseId !== 'all') {
      list = list.filter((p) => p.enterpriseId === enterpriseId);
    }
    if (status && status !== 'all') {
      list = list.filter((p) => p.status === status);
    }
    if (keyword) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(keyword) ||
          p.code?.toLowerCase().includes(keyword) ||
          p.productCode.toLowerCase().includes(keyword) ||
          p.enterpriseName.toLowerCase().includes(keyword),
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.post('/api/products', async ({ request }) => {
    const body = (await request.json()) as Partial<ProductItem>;
    const targetEnterprise = enterprisesStore.find((e) => e.id === body.enterpriseId) || enterprisesStore[0];
    const newProd: ProductItem = {
      id: `prod_${Date.now()}`,
      name: body.name || '新有机胚芽米产品',
      code: body.code || `PROD-ORG-${Date.now().toString().slice(-4)}`,
      enterpriseId: targetEnterprise?.id || 'ent_01',
      enterpriseName: targetEnterprise?.name || '五常市金穗生态大米加工有限公司',
      enterpriseCode: targetEnterprise?.enterpriseCode || 'MA.156.A1.0058',
      productCode: body.productCode || `AG.${String(productsStore.length + 101)}`,
      weight: body.weight || 5,
      unit: body.unit || 'kg',
      germRetentionRate: body.germRetentionRate || 85,
      packageType: body.packageType || '真空礼盒装',
      shelfLifeDays: body.shelfLifeDays || 365,
      organicMark: body.organicMark || 'ORG',
      status: body.status || 'active',
      standard: body.standard || 'GB/T 19630 / GB/T 1354',
      remarks: body.remarks || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    productsStore = [newProd, ...productsStore];

    return HttpResponse.json({
      code: 200,
      message: '产品规格已创建并成功绑定产品码段',
      data: newProd,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.put('/api/products/:id', async ({ params, request }) => {
    const body = (await request.json()) as Partial<ProductItem>;
    const idx = productsStore.findIndex((p) => p.id === params.id);
    if (idx !== -1) {
      productsStore[idx] = {
        ...productsStore[idx]!,
        ...body,
        updatedAt: new Date().toISOString(),
      };
      return HttpResponse.json({
        code: 200,
        message: '产品档案更新成功',
        data: productsStore[idx],
        success: true,
        timestamp: new Date().toISOString(),
      });
    }
    return HttpResponse.json({ code: 404, message: 'Product not found', success: false }, { status: 404 });
  }),

  http.delete('/api/products/:id', ({ params }) => {
    productsStore = productsStore.filter((p) => p.id !== params.id);
    return HttpResponse.json({
      code: 200,
      message: '产品已删除',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Backward compatibility alias for /api/oem/brands
  http.get('/api/oem/brands', () => {
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: enterprisesStore,
        total: enterprisesStore.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Batches and Quality handlers
  http.get('/api/processing/batches', () => {
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: batchesStore,
        total: batchesStore.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.post('/api/processing/batches', async ({ request }) => {
    const body = (await request.json()) as Partial<ProcessingBatch>;
    const newBatch: ProcessingBatch = {
      id: `batch_${Date.now()}`,
      batchNumber: body.batchNumber || `PROC-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(batchesStore.length + 1).padStart(3, '0')}`,
      brandId: body.brandId || 'ent_01',
      brandName: body.brandName || '北米臻选 · 有机胚芽米',
      productSpecId: body.productSpecId || 'prod_01',
      productSpecName: body.productSpecName || '5kg 礼盒装',
      rawPaddyBatchNumber: body.rawPaddyBatchNumber || 'PADDY-20260820-001',
      rawPaddyWeight: body.rawPaddyWeight || 10000,
      landId: body.landId || 'land_01',
      landName: body.landName || '五常核心有机基地 · 1号稻田A区',
      harvestYear: 2026,
      outputWeight: body.outputWeight || 6600,
      yieldRate: body.yieldRate || 66.0,
      processingDate: body.processingDate || new Date().toISOString(),
      workshopName: body.workshopName || '五常金穗 GMP车间',
      manager: body.manager || '孙海峰',
      status: body.status || 'processing',
      craftParameters: body.craftParameters || [],
      traceCodesGenerated: false,
      traceCodesCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    batchesStore = [newBatch, ...batchesStore];

    return HttpResponse.json({
      code: 200,
      message: '加工批次创建成功',
      data: newBatch,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // ================= 4. 订制销售订单 API (/api/processing/orders) =================
  http.get('/api/processing/orders', ({ request }) => {
    const url = new URL(request.url);
    const headerEnterpriseId = request.headers.get('X-Tenant-Enterprise-Id');
    const enterpriseId = url.searchParams.get('enterpriseId') || (headerEnterpriseId && headerEnterpriseId !== 'all' ? headerEnterpriseId : null);
    const keyword = url.searchParams.get('keyword')?.toLowerCase();
    const status = url.searchParams.get('status');
    const startDate = url.searchParams.get('startDate');
    const endDate = url.searchParams.get('endDate');

    let list = [...getOrdersStore()];
    if (enterpriseId && enterpriseId !== 'all') {
      list = list.filter((o) => String(o.enterpriseId) === String(enterpriseId));
    }
    if (status && status !== 'all') {
      list = list.filter((o) => o.status === status);
    }
    if (startDate) {
      list = list.filter((o) => (o.deliveryDate || '').slice(0, 10) >= startDate);
    }
    if (endDate) {
      list = list.filter((o) => (o.deliveryDate || '').slice(0, 10) <= endDate);
    }
    if (keyword) {
      list = list.filter(
        (o) =>
          o.orderNumber?.toLowerCase().includes(keyword) ||
          o.customerName?.toLowerCase().includes(keyword) ||
          o.productName?.toLowerCase().includes(keyword) ||
          o.contactPerson?.toLowerCase().includes(keyword),
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.get('/api/processing/orders/stats', ({ request }) => {
    const url = new URL(request.url);
    const headerEnterpriseId = request.headers.get('X-Tenant-Enterprise-Id');
    const enterpriseId = url.searchParams.get('enterpriseId') || (headerEnterpriseId && headerEnterpriseId !== 'all' ? headerEnterpriseId : null);

    let list = [...getOrdersStore()];
    if (enterpriseId && enterpriseId !== 'all') {
      list = list.filter((o) => String(o.enterpriseId) === String(enterpriseId));
    }

    const totalOrders = list.length;
    const inProductionOrders = list.filter((o) => o.status === 'in_production').length;
    const completedOrders = list.filter((o) => o.status === 'completed').length;
    const pendingCodeOrders = list.filter((o) => o.status === 'pending_code').length;
    const totalQuantity = list.reduce((acc, cur) => acc + (cur.orderQuantity || 0), 0);

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        totalOrders,
        inProductionOrders,
        completedOrders,
        pendingCodeOrders,
        totalQuantity,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.get('/api/processing/orders/:id/trace-tree', ({ params }) => {
    const order = getOrdersStore().find((o) => String(o.id) === String(params.id)) || getOrdersStore()[0];
    if (!order) {
      return HttpResponse.json({ code: 404, message: 'Order not found', success: false }, { status: 404 });
    }

    // 匹配当前订单的所有 MES 加工任务
    let procTasks = processingTasksStore.filter(
      (t) => String(t.orderId) === String(order.id) || t.orderNumber === order.orderNumber,
    );
    if (procTasks.length === 0) {
      procTasks = processingTasksStore.slice(0, 2);
    }

    const prodQcReports: any[] = [];
    const germQcReports: any[] = [];
    const procQcReports: any[] = [];
    const uniqueProdIds = new Set<string>();

    const enrichedProcTasks = procTasks.map((pt) => {
      if (pt.qcReportUrl) {
        procQcReports.push({
          taskCode: pt.taskCode,
          title: `成品出厂全项质检报告 (${pt.productName})`,
          inspector: pt.qcInspector || '张建华',
          qcTime: pt.qcTime || '2026-08-25 18:00:00',
          qcResult: pt.qcResult || 'excellent',
          url: pt.qcReportUrl,
          filename: pt.qcReportName || `${pt.taskCode}_出厂全项质检报告.pdf`,
          size: pt.qcReportSize || 5120000,
        });
      }

      // 处理净粮投料明细
      let materials = pt.materialItems || [];
      if (materials.length === 0 && (pt.productionTaskId || pt.productionTaskCode)) {
        const prod = productionTasksStore.find(
          (p) => String(p.id) === String(pt.productionTaskId) || p.taskCode === pt.productionTaskCode,
        ) || productionTasksStore[0];
        if (prod) {
          materials = [
            {
              id: `mat_auto_${pt.id}`,
              processingTaskId: pt.id,
              productionTaskId: prod.id,
              productionTaskCode: prod.taskCode,
              harvestBatchNo: prod.harvestBatchNo,
              riceVariety: prod.riceVariety,
              landName: prod.landName,
              usedGrainWeightKg: 10000,
            },
          ];
        }
      }

      const enrichedMaterials = materials.map((m: any) => {
        const prod = productionTasksStore.find(
          (p) => String(p.id) === String(m.productionTaskId) || p.taskCode === m.productionTaskCode,
        );
        if (prod && !uniqueProdIds.has(String(prod.id))) {
          uniqueProdIds.add(String(prod.id));
          // 原粮入厂质检报告
          if (prod.qcReportUrl) {
            prodQcReports.push({
              taskCode: prod.taskCode,
              title: `原粮进厂508项农残全项质检报告 (${prod.riceVariety})`,
              inspector: prod.qcInspector || '张建华',
              qcTime: prod.qcTime || '2026-08-21 18:00:00',
              qcResult: prod.qcResult || 'excellent',
              url: prod.qcReportUrl,
              filename: prod.qcReportName || `${prod.taskCode}_原粮进厂质检报告.pdf`,
              size: prod.qcReportSize || 3840000,
            });
          }
          // 胚芽米初碾留胚与纯度质检报告
          if (prod.germQcReportUrl || prod.germQcReportName) {
            germQcReports.push({
              taskCode: prod.taskCode,
              title: `初碾胚芽米留胚率与纯度质检报告 (${prod.riceVariety})`,
              inspector: prod.germQcInspector || prod.qcInspector || '张建华',
              qcTime: prod.germQcTime || '2026-08-21 18:30:00',
              qcResult: prod.germQcResult || 'excellent',
              url: prod.germQcReportUrl || prod.qcReportUrl,
              filename: prod.germQcReportName || `${prod.taskCode}_初碾胚芽米质检报告.pdf`,
              size: prod.germQcReportSize || 3120000,
            });
          }
        }
        return {
          ...m,
          productionTask: prod,
        };
      });

      return {
        ...pt,
        materialItems: enrichedMaterials,
      };
    });

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        order,
        processingTasks: enrichedProcTasks,
        // V3.0：生产任务与加工任务平级，直接挂订单（不再通过投料明细嵌套）
        productionTasks: productionTasksStore.filter(
          (p: any) => !p.orderId || String(p.orderId) === String(order.id),
        ),
        totalProcessingCount: enrichedProcTasks.length,
        totalProductionTaskCount: productionTasksStore.length,
        totalProductionBatchesCount: uniqueProdIds.size || 2,
        qcReports: {
          productionQcReports: prodQcReports,
          germRiceQcReports: germQcReports,
          processingQcReports: procQcReports,
        },
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // ================= 5. MES 生产任务 API (/api/processing/production-tasks) =================
  http.get('/api/processing/production-tasks', ({ request }) => {
    const url = new URL(request.url);
    const keyword = url.searchParams.get('keyword')?.toLowerCase();
    const status = url.searchParams.get('status');
    const priority = url.searchParams.get('priority');
    const startDate = url.searchParams.get('startDate');
    const endDate = url.searchParams.get('endDate');

    let list = [...productionTasksStore];
    if (status && status !== 'all') {
      list = list.filter((t) => t.status === status);
    }
    if (priority && priority !== 'all') {
      list = list.filter((t) => t.priority === priority);
    }
    if (startDate) {
      list = list.filter((t) => (t.planStartTime || '').slice(0, 10) >= startDate);
    }
    if (endDate) {
      list = list.filter((t) => (t.planStartTime || '').slice(0, 10) <= endDate);
    }
    if (keyword) {
      list = list.filter(
        (t) =>
          t.taskCode?.toLowerCase().includes(keyword) ||
          t.taskName?.toLowerCase().includes(keyword) ||
          t.landName?.toLowerCase().includes(keyword) ||
          t.riceVariety?.toLowerCase().includes(keyword) ||
          t.harvestBatchNo?.toLowerCase().includes(keyword),
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.get('/api/processing/production-tasks/stats', () => {
    const totalTasks = productionTasksStore.length;
    const completedTasks = productionTasksStore.filter((t) => t.status === 'completed').length;
    const inProgressTasks = productionTasksStore.filter((t) => t.status === 'in_progress').length;
    const totalRawWeightKg = productionTasksStore.reduce((acc, cur) => acc + (cur.rawGrainWeightKg || 0), 0);
    const totalOutputWeightKg = productionTasksStore.reduce((acc, cur) => acc + (cur.outputWeightKg || 0), 0);
    const qcPassCount = productionTasksStore.filter((t) => t.qcResult === 'pass' || t.qcResult === 'excellent').length;
    const qcPassRate = totalTasks > 0 ? `${((qcPassCount * 100) / totalTasks).toFixed(1)}%` : '100.0%';

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        totalTasks,
        completedTasks,
        inProgressTasks,
        totalRawWeightKg,
        totalOutputWeightKg,
        qcPassRate,
        avgGermRetention: '89.2%',
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.post('/api/processing/production-tasks', async ({ request }) => {
    const body = (await request.json()) as Partial<MesProductionTask>;
    const newTask: any = {
      id: String(Date.now()),
      taskCode: body.taskCode || `MES-PROD-${Date.now().toString().slice(-6)}`,
      taskName: body.taskName || '五常1号田有机水稻原粮脱壳清选工单',
      priority: body.priority || 'high',
      landName: body.landName || '五常核心有机基地 · 1号稻田A区',
      riceVariety: body.riceVariety || '五优稻4号 (稻花香2号)',
      harvestBatchNo: body.harvestBatchNo || 'PADDY-20260820-001',
      rawGrainWeightKg: body.rawGrainWeightKg || 30000,
      outputWeightKg: body.outputWeightKg || 19800,
      millingRate: body.millingRate || '66.0%',
      germRetention: body.germRetention || '88.5%',
      moistureRate: body.moistureRate || '14.0%',
      hullingPressure: body.hullingPressure || '0.35 MPa',
      workshopName: body.workshopName || '五常核心示范区 · 1号原粮脱壳清选车间',
      qcInspector: body.qcInspector || '张建华',
      qcResult: body.qcResult || 'excellent',
      qcReportUrl: body.qcReportUrl || '',
      qcReportName: body.qcReportName || '五常1号田原粮进厂508项农残全项质检报告.pdf',
      qcReportSize: body.qcReportSize || 3840000,
      germQcInspector: body.germQcInspector || '张建华',
      germQcResult: body.germQcResult || 'excellent',
      germQcReportUrl: body.germQcReportUrl || '',
      germQcReportName: body.germQcReportName || '五常1号田初碾胚芽米留胚率与纯度质检报告.pdf',
      germQcReportSize: body.germQcReportSize || 2560000,
      status: body.status || 'completed',
      syncSource: 'manual',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    productionTasksStore = [newTask, ...productionTasksStore];

    return HttpResponse.json({
      code: 200,
      message: 'MES粗加工生产工单已创建',
      data: newTask,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.put('/api/processing/production-tasks/:id', async ({ params, request }) => {
    const body = (await request.json()) as Partial<MesProductionTask>;
    const idx = productionTasksStore.findIndex((t) => String(t.id) === String(params.id));
    if (idx !== -1) {
      productionTasksStore[idx] = {
        ...productionTasksStore[idx],
        ...body,
        updatedAt: new Date().toISOString(),
      };
      return HttpResponse.json({
        code: 200,
        message: 'MES粗加工工单已更新',
        data: productionTasksStore[idx],
        success: true,
        timestamp: new Date().toISOString(),
      });
    }
    return HttpResponse.json({ code: 404, message: 'Production task not found', success: false }, { status: 404 });
  }),

  http.delete('/api/processing/production-tasks/:id', ({ params }) => {
    productionTasksStore = productionTasksStore.filter((t) => String(t.id) !== String(params.id));
    return HttpResponse.json({
      code: 200,
      message: '工单已删除',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // ================= 6. MES 加工任务 API (/api/processing/processing-tasks) =================
  http.get('/api/processing/processing-tasks', ({ request }) => {
    const url = new URL(request.url);
    const headerEnterpriseId = request.headers.get('X-Tenant-Enterprise-Id');
    const enterpriseId = url.searchParams.get('enterpriseId') || (headerEnterpriseId && headerEnterpriseId !== 'all' ? headerEnterpriseId : null);
    const keyword = url.searchParams.get('keyword')?.toLowerCase();
    const status = url.searchParams.get('status');
    const startDate = url.searchParams.get('startDate');
    const endDate = url.searchParams.get('endDate');

    let list = [...processingTasksStore];
    if (enterpriseId && enterpriseId !== 'all') {
      list = list.filter((t) => String(t.enterpriseId) === String(enterpriseId));
    }
    if (status && status !== 'all') {
      list = list.filter((t) => t.status === status);
    }
    if (startDate) {
      list = list.filter((t) => (t.planStartTime || '').slice(0, 10) >= startDate);
    }
    if (endDate) {
      list = list.filter((t) => (t.planStartTime || '').slice(0, 10) <= endDate);
    }
    if (keyword) {
      list = list.filter(
        (t) =>
          t.taskCode?.toLowerCase().includes(keyword) ||
          t.taskName?.toLowerCase().includes(keyword) ||
          t.productName?.toLowerCase().includes(keyword) ||
          t.orderNumber?.toLowerCase().includes(keyword) ||
          t.operator?.toLowerCase().includes(keyword),
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.get('/api/processing/processing-tasks/stats', ({ request }) => {
    const url = new URL(request.url);
    const headerEnterpriseId = request.headers.get('X-Tenant-Enterprise-Id');
    const enterpriseId = url.searchParams.get('enterpriseId') || (headerEnterpriseId && headerEnterpriseId !== 'all' ? headerEnterpriseId : null);

    let list = [...processingTasksStore];
    if (enterpriseId && enterpriseId !== 'all') {
      list = list.filter((t) => String(t.enterpriseId) === String(enterpriseId));
    }

    const totalTasks = list.length;
    const completedTasks = list.filter((t) => t.status === 'completed').length;
    const processingTasks = list.filter((t) => t.status === 'processing').length;
    const finishedQuantity = list.reduce((acc, cur) => acc + (cur.finishedQuantity || 0), 0);
    const qcPassCount = list.filter((t) => t.qcResult === 'pass' || t.qcResult === 'excellent').length;
    const qcPassRate = totalTasks > 0 ? `${((qcPassCount * 100) / totalTasks).toFixed(1)}%` : '100.0%';
    const archivedQcReports = list.filter((t) => !!t.qcReportUrl).length;

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        totalTasks,
        completedTasks,
        processingTasks,
        finishedQuantity,
        qcPassRate,
        archivedQcReports,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.post('/api/processing/processing-tasks', async ({ request }) => {
    const body = (await request.json()) as Partial<MesProcessingTask>;
    const newTask: any = {
      id: String(Date.now()),
      taskCode: body.taskCode || `MES-PROC-${Date.now().toString().slice(-6)}`,
      taskName: body.taskName || '北米金胚 5kg 真空六角礼盒装全自动分装与包装工单',
      orderNumber: body.orderNumber || 'ORD-20260826-008',
      enterpriseId: body.enterpriseId || '8001',
      enterpriseName: body.enterpriseName || '五常市龙凤山水稻专业种植示范合作社',
      productName: body.productName || '北米金胚 · 有机胚芽米六角礼盒装',
      packageSpec: body.packageSpec || '5kg (双砖真空锁鲜)',
      planQuantity: body.planQuantity || 2000,
      finishedQuantity: body.finishedQuantity || 2000,
      unit: body.unit || '盒',
      workshopName: body.workshopName || '五常核心示范区 · 低温锁鲜分装车间',
      lineName: body.lineName || '全自动充氮真空双砖包装1号线',
      operator: body.operator || '李春山',
      qcInspector: body.qcInspector || '张建华',
      qcResult: body.qcResult || 'excellent',
      qcReportUrl: body.qcReportUrl || 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200',
      qcReportName: body.qcReportName || '北米金胚有机胚芽米SGS出厂508项全项质检报告.pdf',
      qcReportSize: body.qcReportSize || 5120000,
      status: body.status || 'completed',
      syncSource: 'manual',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    processingTasksStore = [newTask, ...processingTasksStore];

    return HttpResponse.json({
      code: 200,
      message: 'MES精深加工工单已创建',
      data: newTask,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.put('/api/processing/processing-tasks/:id', async ({ params, request }) => {
    const body = (await request.json()) as Partial<MesProcessingTask>;
    const idx = processingTasksStore.findIndex((t) => String(t.id) === String(params.id));
    if (idx !== -1) {
      processingTasksStore[idx] = {
        ...processingTasksStore[idx],
        ...body,
        updatedAt: new Date().toISOString(),
      };
      return HttpResponse.json({
        code: 200,
        message: 'MES加工工单已更新',
        data: processingTasksStore[idx],
        success: true,
        timestamp: new Date().toISOString(),
      });
    }
    return HttpResponse.json({ code: 404, message: 'Processing task not found', success: false }, { status: 404 });
  }),

  http.delete('/api/processing/processing-tasks/:id', ({ params }) => {
    processingTasksStore = processingTasksStore.filter((t) => String(t.id) !== String(params.id));
    return HttpResponse.json({
      code: 200,
      message: '工单已删除',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // ================= 7. MES 数据同步中枢 =================
  http.post('/api/processing/sync/mes', () => {
    const now = new Date().toISOString();
    setOrdersStore(getOrdersStore().map((o) => ({ ...o, syncTime: now })));
    productionTasksStore = productionTasksStore.map((t) => ({ ...t, syncTime: now }));
    processingTasksStore = processingTasksStore.map((t) => ({ ...t, syncTime: now }));

    return HttpResponse.json({
      code: 200,
      message: 'MES/ERP 生产加工数据与质检报告已完成即时同步',
      data: {
        ordersSynced: getOrdersStore().length,
        productionTasksSynced: productionTasksStore.length,
        processingTasksSynced: processingTasksStore.length,
        qcReportsArchived: processingTasksStore.filter((t) => !!t.qcReportUrl).length,
        syncTime: now,
        status: 'success',
        message: 'MES/ERP 主数据及质检报告增量同步完成',
      },
      success: true,
      timestamp: now,
    });
  }),

  http.get('/api/processing/sync/status', () => {
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        autoSyncInterval: '30 分钟 / 周期',
        lastSyncTime: new Date().toISOString(),
        syncEngineStatus: 'healthy',
        mesConnectionStatus: 'connected',
        minioStorageStatus: 'connected',
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),
];
